import { DropResult } from "@hello-pangea/dnd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ActionType, Column, TableConfig } from "../../../types/types";
import styled from "styled-components";
import StyledText from "../../../components/StyledComponents/StyledText";
import DataTable from "../../../components/listing/DataTable";
import { useTheme } from "../../../middleware/ThemeContext";
import {
} from "../../../api/organizationApiSlice";
import { useUpdateContactMutation } from "../../../api/contactApiSlice";
import { useToast } from "../../../components/toast/ToastContext";
import { useSubmitFormMutation } from "../../../api/formsApiSlice";
import { useNavigate, useParams } from "react-router-dom";
import OrganizationModal from "./form/OrganizationModal";
import { contactService } from "../../../api/contactService";
import { organizationsService } from "../../../api/organizationService";

type OrgRole = {
  id: number;
  isPrimary: boolean;
  organization: {
    id: number;
    name: string;
    image: string | null;
    industry: string | null;
    createdAt: string;
  };
  role: {
    id: number;
    name: string;
  };
};
interface orgListings {
  data: {
    contactOrgRoles?: OrgRole[];
    // Add other properties as needed
    [key: string]: any;
  };
  refetch: () => void;
  isLoading?: boolean;
  editOrganization: boolean;
  setEditOrganization: React.Dispatch<React.SetStateAction<boolean>>;
  setHandleSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  handleSubmit: boolean;
  setOrganizationLoading: React.Dispatch<React.SetStateAction<boolean>>;
  userDataRefetch?: () => void;
}



function groupByOrganization(data: OrgRole[]) {
  const grouped: Record<string, any> = {};

  data?.forEach((item) => {
    const orgName = item.organization.name;

    if (!grouped[orgName]) {
      grouped[orgName] = {
        id: item.organization.id,
        organization: { ...item.organization },
        role: [],
        isPrimary: item.isPrimary,
      };
    }

    grouped[orgName].role.push(item.role);
  });

  return Object.values(grouped);
}

const transformOrgData = (data: any[]) => {
  return data?.map((row) => ({
    id: row.id,
    image: row.signedUrl,
    label: row.name,
    value: row.id,
  }));
};

const transformRoleData = (data: any[]) => {
  return data?.map((row) => ({
    label: row.name,
    value: row.id,
  }));
};

const OrganizationsListing = ({ data, refetch, isLoading, setEditOrganization, editOrganization, handleSubmit, setHandleSubmit, setOrganizationLoading, userDataRefetch }: orgListings) => {
  const { theme } = useTheme();  
  const showToast = useToast();
  const { id } = useParams();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  // const [editOrganization, setEditOrganization] = useState(false);
  const [orgAll, setOrgAll] = useState<any[]>([]);
  const [rolesAll, setRolesAll] = useState<any[]>([]);
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);
  const [isFetchingOrg, setIsFetchingOrg] = useState(false);
  const [metadataValues, setMetadataValues] = useState<Record<string, any>>({});
  const [metadataErrors, setMetadataErrors] = useState<Record<string, string>>(
    {}
  );
  const [selectedOrgOption, setSelectedOrgOption] = useState<any | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchRoles, setSearchRoles] = useState("");
  const navigate = useNavigate();
  const [updateOrgRoles, { isLoading: isLoadingUpdateOrg }] =
    useUpdateContactMutation();

  const [allRolesData, setAllRolesData] = useState<any[]>([]);
  const [isLoadingRol, setIsLoadingRol] = useState(false);
  const [isFetchingRoles, setIsFetchingRoles] = useState(false);
  const submitFormData = useSubmitFormMutation()[0];

  useEffect(() => {
    let cancelled = false;

    const loadOrganizations = async () => {
      try {
        setIsLoadingOrgs(true);
        const response = await organizationsService.getOrganizationsMinimal();
        if (cancelled) return;

        const organizations = Array.isArray(response?.organizations)
          ? response.organizations
          : Array.isArray(response)
            ? response
            : [];

        setOrgAll(organizations);
      } finally {
        if (!cancelled) {
          setIsLoadingOrgs(false);
        }
      }
    };

    void loadOrganizations();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadRoles = async () => {
      if (!editOrganization) {
        setAllRolesData([]);
        setIsLoadingRol(false);
        setIsFetchingRoles(false);
        return;
      }

      try {
        setIsLoadingRol(allRolesData.length === 0);
        setIsFetchingRoles(true);

        const response = await contactService.getContactRoles();
        if (cancelled) {
          return;
        }

        const roles = Array.isArray(response?.roles)
          ? response.roles
          : Array.isArray(response)
            ? response
            : [];

        setAllRolesData(roles);
      } finally {
        if (!cancelled) {
          setIsLoadingRol(false);
          setIsFetchingRoles(false);
        }
      }
    };

    void loadRoles();

    return () => {
      cancelled = true;
    };
  }, [editOrganization]);

  const my_data = useMemo(
    () => data?.contactOrganizations ?? [],
    [data?.contactOrganizations]
  );

  const OrgAll = useMemo(() => transformOrgData(orgAll), [orgAll]);

  const RolesAll = useMemo(() => {
    const filteredRoles = searchRoles
      ? allRolesData.filter((role: any) =>
          String(role?.name ?? "")
            .toLowerCase()
            .includes(searchRoles.toLowerCase())
        )
      : allRolesData;

    return transformRoleData(filteredRoles);
  }, [allRolesData, searchRoles]);


  const LoadMoreOrg = async () => {
    setIsFetchingMore(false);
  };

  const handleRolesSearch = useCallback((value: string) => {
    setSearchRoles(value);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);


  useEffect(() => {
    const fetchOrgs = async () => {
      if (metadataValues?.organizationRoles?.length > 0 && editOrganization) {
        for (const r of metadataValues.organizationRoles) {
          try {
            const org = orgAll.find(
              (item) =>
                String(item?.name ?? "").toLowerCase() ===
                String(r.organizationName ?? "").toLowerCase()
            );
            if (org) {
              setOrgAll((prev) => {
                if (!prev.find((o) => o.id === org.id)) return [...prev, org];
                return prev;
              });
            }
          } catch (err) {
            showToast("Error", "Failed to to fetch organizations", "error");
          }
        }
      }
    };

    fetchOrgs();
  }, [metadataValues?.organizationRoles, editOrganization, orgAll]);

  const addOrgRoleRow = () => {
    setMetadataValues((prev) => {
      const roles = Array.isArray(prev.organizationRoles)
        ? [...prev.organizationRoles]
        : [];
      return {
        ...prev,
        organizationRoles: [
          ...roles,
          { organizationId: "", organizationName: "", rolesId: "" },
        ],
      };
    });
  };

  const removeOrgRoleRow = (rowIndex: number) => {
    setMetadataValues((prev) => {
      const roles = Array.isArray(prev.organizationRoles)
        ? [...prev.organizationRoles]
        : [];
      if (roles.length <= 1) return prev;
      roles.splice(rowIndex, 1);
      return { ...prev, organizationRoles: roles };
    });
  };

  const handleOrgRoleChange = (
    rowIndex: number,
    field: "org" | "role",
    value: any
  ) => {
    setMetadataValues((prev) => {
      const roles = Array.isArray(prev.organizationRoles)
        ? [...prev.organizationRoles]
        : [{ organizationId: "", organizationName: "", rolesId: "" }];
      if (!roles[rowIndex])
        roles[rowIndex] = { organizationId: "", organizationName: "", rolesId: "" };

      if (field === "org") {
        const orgId = value?.id ?? value?.value ?? value;
        const orgName = value?.name ?? value?.label ?? String(orgId);
        roles[rowIndex].organizationId = orgId;
        roles[rowIndex].organizationName = orgName;
        roles[rowIndex].rolesId = "";
      } else {
        roles[rowIndex].rolesId = value;
      }

      return { ...prev, organizationRoles: roles };
    });
    const errorKey = `organizationRoles_${field}_${rowIndex}`;
    if (metadataErrors[errorKey]) {
      setMetadataErrors((prev) => {
        const next = { ...prev };
        delete next[errorKey];
        return next;
      });
    }
  };

  const handleIsPrimaryChange = (rowIndex: number) => {
    setMetadataValues((prev) => {
      const roles = Array.isArray(prev.organizationRoles)
        ? [...prev.organizationRoles]
        : [];
      const updatedRoles = roles.map((role, idx) => ({
        ...role,
        isPrimary: idx === rowIndex, // ✅ only clicked row true
      }));
      return { ...prev, organizationRoles: updatedRoles };
    });
  };

  //   const handleOrgRoleChange = () => {}

  const columns: Column[] = [
    { id: "name", label: "Organization Name", isSortable: false },
    { id: "industry", label: "Industry", isSortable: false },
    { id: "createdAt", label: "Created Date", isSortable: false },
    { id: "roles", label: "Relation To Company", isSortable: false, isCustomStandardTags: true, isTag: true },
    { id: "membership", label: "Membership type", isSortable: false },

    // { id: "isPrimary", label: "Is Primary", isSortable: false },
  ];

  const tableData = (my_data ?? []).map((org: any) => ({
    name: org?.organization?.name,
    signedUrl: org?.organization?.signedUrl,
    id: org?.organization?.id,
    roles: org?.contactOrgRoles.map((row: any) => row?.role?.name).join(", "),
    isPrimary: org?.isPrimary,
    organization_id: org?.organization?.id,
    createdAt: org?.organization?.createdAt,
  }));

  const STORAGE_KEY = `tableConfig_companies`;
  const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    return saved
      ? JSON.parse(saved)
      : {
        visibleColumns: columns
          .filter((col) => col.id !== "id")
          .map((col) => col.id),
        page: 1,
        perPage: 10,
        sort: undefined,
        filters: {},
        query: "",
        columnOrder: columns.map((col) => col.id),
      };
  });

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const newColumnOrder = Array.from(tableConfig.columnOrder);
      const [removed] = newColumnOrder.splice(result.source.index, 1);
      newColumnOrder.splice(result.destination.index, 0, removed);
      setTableConfig((prev) => ({ ...prev, columnOrder: newColumnOrder }));
    },
    [tableConfig.columnOrder]
  );

  const validateMetadata = (): boolean => {
    let isValid = true;
    const newErrors: Record<string, string> = {};

    const rows =
      (metadataValues.organizationRoles as {
        organizationId: any;
        rolesId: any;
      }[]) || [];

    rows.forEach((r, idx) => {
      if (r.organizationId && (!r.rolesId || r.rolesId.length === 0)) {
        newErrors["organizationRoles_role_" + idx] =
          "Role is required for selected organization";
        isValid = false;
      }
    });

    setMetadataErrors(newErrors);
    return isValid;
  };

  const buildPayload = () => {
    const value = {
      id: data?.id,
      contactMeta: {
        // email: data?.email,
        organizationRoles: (metadataValues.organizationRoles || [])
          .filter((r: any) => r.organizationId)
          .map((r: any) => ({
            organizationId: r.organizationId,
            rolesId: Array.isArray(r.rolesId)
              ? r.rolesId.map((role: any) => typeof role === 'object' ? role.value ?? role.id : role).filter((val: any) => val !== undefined && val !== null && val !== "")
              : r.rolesId
                ? [typeof r.rolesId === "object" ? (r.rolesId as any).value ?? (r.rolesId as any).id : r.rolesId]
                : [],
            isPrimary: r.isPrimary,
          })),
      },
    };

    return value;
  };
  const onHandelSubmit = () => {
    setOrganizationLoading(true);
    const isValid = validateMetadata();
    if (!isValid) {
      return;
    }

    const payload = buildPayload();

    updateOrgRoles(payload)
      .unwrap()
      .then((res) => {
        showToast(
          "Successfully",
          "Organizations updated successfully",
          "success"
        );
        setEditOrganization(false);
        refetch && refetch();
        userDataRefetch && userDataRefetch();
        setHandleSubmit(false);
        setOrganizationLoading(false);
      })
      .catch((err) => {
        showToast(
          "Error",
          err?.data?.message || "Failed to update organizations",
          "error"
        );
        setHandleSubmit(false);
        setOrganizationLoading(false);
      });
  };

  const addOrganization = async (orgName: string) => {
    try {
      const payload = {
        metaData: {
          name: orgName,
        },
        scope: "ORGANIZATION",
      };
      const result = await submitFormData(payload).unwrap();

      const newOrgId = result?.entityId; // Select new Organization ID

      if (!newOrgId) {
        throw new Error("Organization created but ID not returned");
      }
      showToast("Success", "Organization Added successfully", "success");
      // refetchOrg();
      return newOrgId; // Return selected ID
    } catch (err: any) {
      const errorMessage =
        err?.data?.message ||
        err?.message ||
        (err?.status === 401
          ? "Session expired. Please log in again."
          : "Submission failed");
      showToast("Error", errorMessage, "error");
    } finally {
      // setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (my_data && my_data.length > 0) {
      const initialMetadataValues = {
        organizationRoles: my_data.map((field: any) => ({
          organizationId: field?.organization?.id,
          organizationName: field?.organization?.name,
          rolesId: field?.contactOrgRoles.map((row: any) => row?.role?.id),
          isPrimary: field?.isPrimary,
        })),
      };

      setMetadataValues(initialMetadataValues);
    } else {
      setMetadataValues({
        organizationRoles: [
          {
            organizationId: "",
            organizationName: "",
            rolesId: [],
            isPrimary: true,
          },
        ],
      });
    }
  }, [my_data]);

  const handleAction = useCallback(async (action: ActionType, id: string) => {
    const numericId = Number(id);

    if (action === 'view') {
      navigate(`/home/organizations/${numericId}`);
    }
  }, [navigate]);

  return (
    <div className="mb-2">
      <div className="d-flex align-items-center justify-content-between">
      </div>
      <DataTable
        profiledName
        hideActionColumn={true}
        noAction
        columns={columns}
        data={tableData}
        title="Organizations Listing"
        columnOrder={tableConfig.columnOrder}
        visibleColumns={tableConfig.visibleColumns}
        sort={tableConfig.sort}
        noDataTitle="No Organization Found"
        noDataSubtitle="Your Organization list is currently empty. Start adding new organization to keep everything organized and easy to find."
        onDragEnd={handleDragEnd}
        loading={isLoading ?? false}
        perPage={tableConfig.perPage}
        onAction={() => { }}
        minWidth="1000px"
        containerHeight={500}
      />

      <OrganizationModal isLoadingUpdateOrg={isLoadingUpdateOrg} isFetchingOrg={isFetchingOrg} handleSubmit={onHandelSubmit} addOrganization={addOrganization} handleRolesSearch={handleRolesSearch} handleIsPrimaryChange={handleIsPrimaryChange} metadataErrors={metadataErrors} removeOrgRoleRow={removeOrgRoleRow} addOrgRoleRow={addOrgRoleRow} RolesAll={RolesAll} handleSearch={handleSearch} LoadMoreOrg={LoadMoreOrg} isOpen={editOrganization} onClose={() => setEditOrganization(false)} metadataValues={metadataValues} OrgAll={OrgAll} handleOrgRoleChange={handleOrgRoleChange} allOrg={orgAll} isLoadingRol={isLoadingRol} />
    </div>
  );
};

const StyledBox = styled.div<{ bgColor?: string }>`
  border-radius: 5px;
  background: ${({ bgColor, theme }) =>
    bgColor ? bgColor : theme.CorporateTheme?.colors?.background};
  padding-bottom: 5px;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  box-shadow: 0px 2px 10px 0px #0000000d;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
`;

export const FieldLabel = styled(StyledText)`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
`;

const OrgRoleCont = styled("div")`
  & .row {
    padding: 20px 0px;
  }
  & .row:not(:last-child) {
    border-bottom: 1px solid
      ${({ theme }) => theme.CorporateTheme.colors.border};
  }
`;

export default OrganizationsListing;
